'use client';

import type { CSSProperties } from 'react';
import Link from 'next/link';
import { IconArrowRight, IconLock } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { GPUProduct } from '@/lib/data/gpu-products';
import { GPU_PRODUCTS, formatHourlyRate, getUnavailableLabel } from '@/lib/data/gpu-products';
import { GpuCharacter, HUES, personaFor } from '@/components/shop/homepage/gpu-characters';
import { useCaseHue } from './arcade-classes';
import { HoverPixels, LOCKED_PERSONA, Platform, tiltHandlers } from './arcade-art';

/** The catalogue's tier, shown as the item's level. */
const TIER_LEVEL: Record<GPUProduct['tier'], string> = {
  entry: 'Lv 1 · Entry',
  mid: 'Lv 2 · Mid',
  high: 'Lv 3 · High',
  premium: 'Lv 4 · Premium',
};

/** Meter scale: the largest value in the catalogue, so bars compare across cards. */
const MAX = {
  vram: Math.max(...GPU_PRODUCTS.map((g) => g.vram)),
  vcpus: Math.max(...GPU_PRODUCTS.map((g) => g.vcpus)),
  ram: Math.max(...GPU_PRODUCTS.map((g) => g.ram)),
};

/** The card hue: the GPU's character colour, or grey for one nobody can rent. */
function cardHue(gpu: GPUProduct, index: number): string {
  return gpu.available ? HUES[personaFor(gpu.id, index).hue].cssVar : 'var(--muted-foreground)';
}

interface GPUProductCardProps {
  gpu: GPUProduct;
  /** Position in the catalogue, so a GPU without its own character borrows one. */
  index?: number;
  /**
   * Link into the create form, built with `machineCreateHref()`. Ignored for a
   * product that cannot be rented, which shows its status instead of a CTA.
   */
  rentHref?: string;
  /**
   * Hourly rate to show. Defaults to the catalogue base rate ("from"); the
   * products page passes the regional rate when a region filter is set.
   */
  price?: number;
  /** Caption above the price, e.g. "From" or the selected region. */
  priceLabel?: string;
  className?: string;
}

export function GPUProductCard({
  gpu,
  index = 0,
  rentHref,
  price = gpu.basePrice,
  priceLabel = 'From',
  className,
}: GPUProductCardProps) {
  // Only a rentable product gets a link (and a price).
  const href = gpu.available ? rentHref : undefined;
  const persona = gpu.available ? personaFor(gpu.id, index) : LOCKED_PERSONA;
  const hue = cardHue(gpu, index);
  const model = gpu.name.replace(/^NVIDIA\s+/, '');

  const specs = [
    { label: 'VRAM', value: `${gpu.vram} GB`, fill: gpu.vram / MAX.vram },
    { label: 'vCPUs', value: String(gpu.vcpus), fill: gpu.vcpus / MAX.vcpus },
    { label: 'RAM', value: `${gpu.ram} GB`, fill: gpu.ram / MAX.ram },
  ];

  return (
    <article
      {...tiltHandlers()}
      style={{ '--hue': hue } as CSSProperties}
      className={cn(
        'ga-card ga-tilt group relative flex h-full flex-col overflow-hidden rounded-[1.4rem]',
        !gpu.available && 'ga-card-locked',
        className
      )}
    >
      {/* Stage */}
      <div className="ga-card-stage relative h-56 overflow-hidden">
        <div aria-hidden className="ga-card-grid absolute inset-0" />
        <div aria-hidden className="select-aura absolute left-1/2 top-[48%] size-48 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70 blur-xl" />

        <div className="absolute inset-x-4 top-4 z-10 flex items-start justify-between gap-2">
          <span className="ga-chip-tag rounded-md px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-foreground">
            {TIER_LEVEL[gpu.tier]}
          </span>
          {gpu.available && gpu.badge && (
            <Badge className="ga-rarity rounded-md border-0 px-2 py-1 font-heading text-[11px] font-bold uppercase tracking-wider">
              {gpu.badge}
            </Badge>
          )}
        </div>

        <div aria-hidden className="absolute bottom-3 left-1/2 w-[46%] -translate-x-1/2">
          <div className="ga-card-char relative z-10 mx-auto w-[86%]">
            <GpuCharacter
              persona={persona}
              uid={`arcade-card-${gpu.id}`}
              className="drop-shadow-[0_14px_18px_rgba(0,0,0,0.5)]"
            />
          </div>
          <Platform hue={hue} beam={gpu.available} className="-mt-[8%] h-7 w-full" />
        </div>
        {gpu.available && <HoverPixels />}

        {gpu.available ? (
          <span className="absolute bottom-3 left-4 z-10 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {persona.codename} · {persona.role}
          </span>
        ) : (
          <Badge
            variant="outline"
            className="absolute inset-x-0 bottom-[42%] z-10 mx-auto flex w-fit items-center gap-1.5 rounded-full border-border bg-background/85 px-3 py-1.5 font-heading text-xs font-bold uppercase tracking-wider text-foreground"
          >
            <IconLock className="size-3.5" />
            Locked
          </Badge>
        )}
        <div aria-hidden className="ga-card-sheen pointer-events-none absolute inset-0" />
      </div>

      {/* Body */}
      <div className="relative flex flex-1 flex-col px-5 pb-5 pt-4">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">NVIDIA</p>
        <h3 className="font-heading text-2xl font-bold leading-tight tracking-tight text-foreground">{model}</h3>
        <p className="mt-0.5 truncate text-sm text-muted-foreground">{gpu.consumerEquivalent}</p>

        <dl className="mt-4 space-y-2.5">
          {specs.map((spec) => (
            <div key={spec.label} className="grid grid-cols-[52px_1fr_auto] items-center gap-3">
              <dt className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {spec.label}
              </dt>
              <div aria-hidden className="mission-meter h-2.5 overflow-hidden rounded-[3px]">
                <span
                  className="meter-fill block h-full rounded-[3px]"
                  style={{ width: `${Math.max(8, Math.round(spec.fill * 100))}%` }}
                />
              </div>
              <dd className="min-w-[44px] text-right font-heading text-sm font-bold tabular-nums text-foreground">
                {spec.value}
              </dd>
            </div>
          ))}
        </dl>

        <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Recommended for">
          {gpu.useCases.map((useCase) => (
            <li
              key={useCase}
              style={{ '--hue': gpu.available ? useCaseHue(useCase) : 'var(--muted-foreground)' } as CSSProperties}
              className="ga-chip-tag rounded-md px-2 py-1 text-xs font-medium text-foreground"
            >
              {useCase}
            </li>
          ))}
        </ul>

        <div className="min-h-4 flex-1" />

        <div className="mt-4 border-t border-border/60 pt-4">
          {href ? (
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <span className="power-pulse h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_var(--primary)]" />
                Available to rent
              </p>
              <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {priceLabel}
              </p>
              <p className="font-heading text-2xl font-bold tabular-nums leading-none text-foreground">
                {formatHourlyRate(price)}
              </p>
            </div>
          ) : (
            // No price: a product nobody can rent must not advertise a rate.
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <IconLock className="size-3.5" />
                {getUnavailableLabel(gpu)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {gpu.withdrawn ? 'No longer offered for new machines.' : 'Not yet available to rent.'}
              </p>
            </div>
          )}
        </div>

        {href ? (
          <Button asChild className="ga-cta-hue mt-4 h-12 w-full rounded-xl text-base font-bold hover:bg-transparent">
            <Link href={href}>
              Configure GPU
              <IconArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        ) : (
          <Button disabled variant="outline" className="mt-4 h-12 w-full rounded-xl text-base font-semibold">
            <IconLock className="size-4" />
            Configure GPU
          </Button>
        )}
      </div>
    </article>
  );
}

// Compact variant: a small item slot, used to suggest GPUs when a filter
// leaves the grid empty.
interface GPUProductCardCompactProps {
  gpu: GPUProduct;
  index?: number;
  /** Link into the create form; ignored when the product cannot be rented. */
  rentHref?: string;
  className?: string;
}

export function GPUProductCardCompact({ gpu, index = 0, rentHref, className }: GPUProductCardCompactProps) {
  const href = gpu.available ? rentHref : undefined;
  const persona = gpu.available ? personaFor(gpu.id, index) : LOCKED_PERSONA;

  const body = (
    <div className="flex items-center gap-3">
      <div aria-hidden className="w-12 shrink-0 transition-transform duration-300 group-hover:-translate-y-1">
        <GpuCharacter persona={persona} uid={`arcade-compact-${gpu.id}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-heading text-sm font-bold text-foreground">{gpu.name.replace(/^NVIDIA\s+/, '')}</p>
        <p className="font-mono text-[11px] tabular-nums text-muted-foreground">
          {gpu.vram} GB VRAM · {gpu.vcpus} vCPUs
        </p>
      </div>
      {href ? (
        <span className="flex items-center gap-1 font-heading text-sm font-bold tabular-nums text-foreground">
          {formatHourlyRate(gpu.basePrice)}
          <IconArrowRight className="size-4 text-[var(--hue)]" />
        </span>
      ) : (
        <Badge variant="muted" className="text-[10px] uppercase tracking-wider">
          {getUnavailableLabel(gpu)}
        </Badge>
      )}
    </div>
  );

  const shell = cn('ga-card group relative block rounded-xl p-3 text-left', className);
  const style = { '--hue': cardHue(gpu, index) } as CSSProperties;

  return href ? (
    <Link href={href} className={shell} style={style}>
      {body}
    </Link>
  ) : (
    <div className={shell} style={style}>
      {body}
    </div>
  );
}
